import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "../context/I18nContext";
import { requestPasswordReset, verifyPasswordResetOtp } from "../services/auth";
import "./Auth.css";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("request");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequest = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setStatus(
        t("authPage.forgotSuccess", {
          defaultValue: "If the email exists, a code was sent.",
        }),
      );
      setStep("verify");
    } catch (err) {
      setError(err.message || t("authPage.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    setError("");
    setStatus("");

    if (!otp.trim()) {
      setError(
        t("authPage.otpRequired", { defaultValue: "OTP code is required." }),
      );
      return;
    }

    setLoading(true);
    try {
      const payload = await verifyPasswordResetOtp({
        email,
        otp: otp.trim(),
      });
      const token = payload?.token;
      if (!token) {
        throw new Error(
          t("authPage.loginError", {
            defaultValue: "Something went wrong. Please try again.",
          }),
        );
      }

      try {
        sessionStorage.setItem("casaclass:passwordResetEmail", email);
        sessionStorage.setItem("casaclass:passwordResetToken", token);
      } catch (e) {
        // Ignore storage errors.
      }

      navigate("/reset-password", {
        replace: true,
        state: { email, token },
      });
    } catch (err) {
      setError(err.message || t("authPage.loginError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-shell">
      <div className="auth-card">
        <h1>{t("authPage.forgotTitle")}</h1>
        <p>{t("authPage.forgotLead")}</p>

        {status && <div className="auth-success">{status}</div>}
        {error && <div className="auth-error">{error}</div>}

        {step === "request" ? (
          <form className="auth-form" onSubmit={handleRequest}>
            <div className="auth-field">
              <label htmlFor="email">{t("authPage.email")}</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t("authPage.emailPlaceholder")}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>

            <div className="auth-actions">
              <button type="submit" className="btn primary" disabled={loading}>
                {loading
                  ? t("authPage.forgotSending")
                  : t("authPage.forgotCta", { defaultValue: "Send code" })}
              </button>
              <div className="auth-links">
                <Link to="/login">{t("authPage.forgotBack")}</Link>
                <span className="separator">•</span>
                <Link to="/signup">{t("authPage.goSignup")}</Link>
              </div>
            </div>
          </form>
        ) : step === "verify" ? (
          <form className="auth-form" onSubmit={handleVerify}>
            <div className="auth-field">
              <label htmlFor="otp">
                {t("authPage.otp", { defaultValue: "OTP code" })}
              </label>
              <input
                id="otp"
                name="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t("authPage.otpPlaceholder", {
                  defaultValue: "Enter the 6-digit code",
                })}
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                required
              />
            </div>

            <div className="auth-actions">
              <button type="submit" className="btn primary" disabled={loading}>
                {loading
                  ? t("authPage.forgotSending", {
                      defaultValue: "Verifying...",
                    })
                  : t("authPage.verifyCta", { defaultValue: "Verify code" })}
              </button>
              <div className="auth-links">
                <button
                  type="button"
                  className="btn secondary"
                  onClick={() => {
                    setStep("request");
                    setStatus("");
                    setError("");
                    setOtp("");
                  }}
                  disabled={loading}
                >
                  {t("authPage.back", { defaultValue: "Back" })}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="auth-actions">
            <div className="auth-links">
              <Link to="/login">{t("authPage.forgotBack")}</Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
